/**
 * SignaturePad — canvas-based signature capture. Exports a PNG data-URL.
 * Small, dependency-free, works on desktop + touch. Used inside the
 * Orientation Portal for parent + student signatures.
 */
import React, { useRef, useState, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';

const SignaturePad = ({ label, onChange, testId }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0A0A0E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#F97316';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const evt = e.touches ? e.touches[0] : e;
    return { x: (evt.clientX - rect.left) * scaleX, y: (evt.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (onChange && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0A0A0E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    if (onChange) onChange('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-black tracking-widest text-zinc-500 uppercase">{label}</span>
        <button type="button" onClick={clear} className="text-[9px] font-black text-zinc-500 hover:text-white inline-flex items-center gap-1" data-testid={`${testId}-clear`}>
          <RotateCcw className="w-2.5 h-2.5" /> CLEAR
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={140}
        className="w-full h-32 border-2 border-zinc-800 cursor-crosshair touch-none bg-[#0A0A0E]"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={testId}
      />
      {empty && <div className="text-[10px] text-zinc-600 italic mt-1">Sign above with your mouse or finger.</div>}
    </div>
  );
};

export default SignaturePad;
